import clsx from 'clsx';
import dayjs from 'dayjs';
import { AxiosError } from 'axios';
import { WisoryLabel, WisoryInput } from 'components/Inputs';
import { NextButton } from 'components/NextButton';
import { PrevButton } from 'components/PrevButton';
import { BookingFormLayout } from 'pages/profile/components/Layout';
import { ProfileFooter } from 'pages/profile/components/Layout/components/ProfileFooter';
import { TimeSpanSection } from './TimeSpanSection';
import { CustomerInfo } from 'pages/profile/types';
import { useForm } from 'react-hook-form';
import { useContext, useState } from 'react';
import { BookingFormContext } from 'pages/profile/context2';
import baseApi, { CalendarEventPlatformType } from 'api';
import RadioButton from 'components/RadioButton';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import { Trans, useTranslation } from 'react-i18next';
import { getWordpressPageUrl } from 'pages/profile/helper';
import SquarespaceLink from 'components/SquarespaceLink';
import { TimeSlotSection } from './TimeSlotSection';

export const ConfirmStep = () => {
  const { advisorName, advisorId, state, setState, setStep, clearUserInfo, videoPlatforms } =
    useContext(BookingFormContext);
  const { t } = useTranslation();
  const { register, handleSubmit } = useForm<CustomerInfo>({
    mode: 'onSubmit',
  });
  const [submittingError, setSubmittingError] = useState<AxiosError<{
    errorCode: string;
    errorMessage: string;
  }> | null>(null);
  const userInfo = state.customerInfo;
  const guestInterestRequest = state.workflow === 'suggest-timespans' && !state.customerInfo.isWisoryUser;
  const submitFn = useMutation(
    // @ts-ignore
    () => {
      if (state.workflow === 'suggest-timespans') {
        const interestParams = {
          advisorId: parseInt(advisorId),
          notes: userInfo.purpose,
          reference: userInfo.reference,
          reason: userInfo.goal,
        };
        if (userInfo.isWisoryUser) {
          return baseApi.interests.registerOfInterest({
            ...interestParams,
            platformType: userInfo.videoPlatform,
            timeSlotRequests: state.suggestedSlots
              .filter((ts) => ts && ts.startDate && ts.endDate)
              .map((ts) => {
                return {
                  startTime: dayjs(ts?.startDate).toISOString(),
                  endTime: dayjs(ts?.endDate).toISOString(),
                };
              }),
          });
        }
        return baseApi.interests.registerOfInterestForAnonymous({
          ...interestParams,
          customerInfo: {
            company: userInfo.customerCompany,
            email: userInfo.customerEmail,
            name: userInfo.customerName,
            phoneNumber: userInfo.customerPhoneNumber,
          },
          billingAddress: userInfo.customerBillingAddress,
          timeSlotRequests: state.suggestedSlots
            .filter((ts) => ts && ts.startDate && ts.endDate)
            .map((ts) => {
              return {
                startTime: dayjs(ts?.startDate).toISOString(),
                endTime: dayjs(ts?.endDate).toISOString(),
              };
            }),
        });
      }

      const selectSlotParams = {
        platformType: userInfo.videoPlatform,
        shortDescription: userInfo.purpose,
        reason: userInfo.goal,
        reference: userInfo.reference,
      };

      return userInfo.isWisoryUser
        ? baseApi.calendarEvents.createCalendarEvent({
            skipVerifyProcess: true,
            appointmentAvailabilityId: state.selectedSlot?.id,
            ...selectSlotParams,
          })
        : baseApi.api.paymentCreateBookingInvoice({
            appointmentAvailabilityId: state.selectedSlot?.id,
            invoiceRecipientInfo: {
              customerCompany: state.customerInfo.customerCompany,
              customerEmail: state.customerInfo.customerEmail,
              customerName: state.customerInfo.customerName,
              customerPhoneNumber: state.customerInfo.customerPhoneNumber,
              customerBillingAddress: state.customerInfo.customerBillingAddress,
            },

            ...selectSlotParams,
          });
    },
    {
      onError(error: AxiosError) {
        const errorResponse = (error as AxiosError).response;
        const data = errorResponse?.data;

        if (errorResponse?.status === 401) {
          toast.error(t('REACT_BOOKING_FRONTEND.booking2.sessionExpired'));
          clearUserInfo();
          setStep('login-or-enter-as-guest');
          return;
        }

        if (data?.errorCode) {
          const errorData = data?.errorData;

          let params = {};

          if (errorData) {
            const date = dayjs(errorData?.sessionDate).format('dddd DD MMM');
            const time = dayjs(errorData?.sessionDate).format('HH:mm');

            params = {
              advisorFullname: errorData?.advisorFullname,
              date,
              time,
            };
          }

          toast.error(t(data.errorCode, params), { autoClose: false });

          return;
        }

        // General Error
        toast.error(
          <Trans
            i18nKey="REACT_BOOKING_FRONTEND.common.generalError"
            t={t}
            components={[<SquarespaceLink to={getWordpressPageUrl('CONTACT_US_URL')} />]}
          />,
          { autoClose: false }
        );

        setSubmittingError(data);
      },
      onSuccess() {
        setStep('thankyou');
      },
    }
  );

  const submit = () => {
    submitFn.mutate();
  };

  const onVideoPlatformChange = (p: CalendarEventPlatformType) => {
    setState((draft) => {
      draft.customerInfo.videoPlatform = p;
    });
  };

  const onReferenceChange = (v: string) => {
    setState((draft) => {
      draft.customerInfo.reference = v;
    });
  };

  return (
    <div>
      <form onSubmit={handleSubmit(submit)} className="text-white mb-0">
        <BookingFormLayout
          footer={
            <ProfileFooter className="flex justify-between">
              <PrevButton
                type="button"
                onClick={() => {
                  setStep(state.previousStep);
                }}
              >
                {t('REACT_BOOKING_FRONTEND.common.backButton')}
              </PrevButton>
              <NextButton type="submit" loading={submitFn.isLoading}>
                {t('REACT_BOOKING_FRONTEND.booking2.completeBooking')}
              </NextButton>
            </ProfileFooter>
          }
        >
          <div className={clsx('wisory-title', 'mb-10')}>{t('REACT_BOOKING_FRONTEND.booking2.confirmStepTitle')}</div>

          <div className="flex flex-col">
            <div className="flex mb-3">
              <WisoryLabel className="w-44 mr-3 mb-0">
                {t('REACT_BOOKING_FRONTEND.common.advisorNameLabel')}
              </WisoryLabel>
              <div className="w-4/5 text-black break-words font-openSans">{advisorName}</div>
            </div>

            {state.workflow === 'select-time' && state.selectedSlot && (
              <>
                <TimeSlotSection slot={state.selectedSlot} />
              </>
            )}

            {state.workflow === 'suggest-timespans' && (
              <>
                <TimeSpanSection advisorName={advisorName} timeslots={state.suggestedSlots} />
              </>
            )}

            <div className="flex mb-3">
              <WisoryLabel className="w-44 mr-3 mb-0">{t('REACT_BOOKING_FRONTEND.common.goalLabel')}</WisoryLabel>
              <div className="w-4/5 text-black break-words font-openSans">{userInfo.goal}</div>
            </div>
            <div className="flex mb-3">
              <WisoryLabel className="w-44 mr-3 mb-0">{t('REACT_BOOKING_FRONTEND.common.purposeLabel')}</WisoryLabel>
              <div className="w-4/5 text-black break-words font-openSans">{userInfo.purpose}</div>
            </div>
            {!guestInterestRequest && (
              <div className="flex mb-3">
                <WisoryLabel className="w-44 mr-3 mb-0">{t('REACT_BOOKING_FRONTEND.common.platformLabel')}</WisoryLabel>
                <div className="w-4/5 text-black break-words">
                  <div className="flex flex-col text-black">
                    {videoPlatforms
                      .filter((v) => v.enabled)
                      .map((v) => {
                        return (
                          <RadioButton
                            className="mb-2"
                            label={v.label}
                            checked={userInfo.videoPlatform === v.value}
                            onChange={() => onVideoPlatformChange(v.value)}
                            black
                            key={v.value}
                          />
                        );
                      })}
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center">
              <WisoryLabel className="w-44 mr-3 mb-0">{t('checkout.reference')}</WisoryLabel>
              <WisoryInput
                className="w-4/5"
                style={{ height: 45 }}
                {...register('reference', {
                  onChange(e) {
                    onReferenceChange(e.target.value);
                  },
                })}
                type="text"
                placeholder={t('checkout.reference')}
              />
            </div>
          </div>
          <div className="mb-8 mt-8 horizontal-line" />
          <div className="flex flex-col">
            <div className="flex mb-3">
              <WisoryLabel className="w-44 mr-3 mb-0">{t('customer_name')}</WisoryLabel>
              <div className="w-4/5 text-black break-words font-openSans">{userInfo.customerName}</div>
            </div>
            <div className="flex mb-3">
              <WisoryLabel className="w-44 mr-3 mb-0">{t('customer_company')}</WisoryLabel>
              <div className="w-4/5 text-black break-words font-openSans">{userInfo.customerCompany}</div>
            </div>
            <div className="flex mb-3">
              <WisoryLabel className="w-44 mr-3 mb-0">{t('customer_email')}</WisoryLabel>
              <div className="w-4/5 text-black break-words font-openSans">{userInfo.customerEmail}</div>
            </div>
            <div className="flex mb-3">
              <WisoryLabel className="w-44 mr-3 break-words mb-0">
                {t('REACT_BOOKING_FRONTEND.customerPhoneNumber')}
              </WisoryLabel>
              <div className="w-4/5 text-black font-openSans">{userInfo.customerPhoneNumber}</div>
            </div>

            {!userInfo.isWisoryUser && (
              <div className="flex mb-3">
                <WisoryLabel className="w-44 mr-3 break-words mb-0">{t('customer_billing_address')}</WisoryLabel>
                <div className="w-4/5 text-black font-openSans">{userInfo.customerBillingAddress}</div>
              </div>
            )}
          </div>

          {submittingError && (
            <div
              className="font-openSans animate__animated animate__shakeX mt-5 text-md text-denim"
              key={submittingError.response?.data.errorCode}
            >
              {submittingError.response?.data.errorCode ? t(submittingError.response?.data.errorCode) : ''}
            </div>
          )}
        </BookingFormLayout>
      </form>
    </div>
  );
};
